import React from 'react';
import { Box, Typography } from '@mui/material';

// Shared presentational primitives for the Clinical Insights / Pedigree Analysis
// sidebar sections. Extracted so the analysis views (and future consumers) share
// one card + header style instead of redefining them per file.

const HOVER_RING_CLASS = 'pa-hover-ring';
const HOVER_COLOR = '#2ba9bf';

// Hover-link (card → canvas): ring-highlight the canvas node(s) for the given
// pids. Appends a transient SVG ring to each node group — never touches the
// node's own styling, so it can't clash with selection / lock / carrier marks.
// Pass on=false (or []) to clear.
export function highlightCanvasNodes(pids = [], on = true) {
  document
    .querySelectorAll('.' + HOVER_RING_CLASS)
    .forEach((el) => el.remove());
  if (!on) return;
  const NS = 'http://www.w3.org/2000/svg';
  pids.forEach((pid) => {
    const g = document.querySelector(`g[data-selection-pid="${pid}"]`);
    if (!g) return;
    // Sit the ring well OUTSIDE the node and its carrier perimeter (the carrier
    // mark is a bold stroke on the node edge, ~SYMBOL_SIZE*0.2 wide), so the
    // highlight never obscures either. For squares we must clear the corners,
    // so enclose the bounding *diagonal*, not the half-side; circles use the
    // radius. `dim * 0.34` margin covers the carrier perimeter + the ring's own
    // (thick) stroke + a small gap.
    let r = 150;
    try {
      const te = g.querySelector('path.te');
      if (te) {
        const b = te.getBBox();
        const dim = Math.max(b.width, b.height) || 175;
        const isRound = /[aA]/.test(te.getAttribute('d') || '');
        const enclosing = isRound ? dim / 2 : (Math.SQRT2 * dim) / 2;
        r = enclosing + dim * 0.34;
      }
    } catch (_) {
      /* getBBox throws if not yet laid out — keep the default radius */
    }
    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('class', HOVER_RING_CLASS);
    ring.setAttribute('cx', '0');
    ring.setAttribute('cy', '0');
    ring.setAttribute('r', String(r));
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', HOVER_COLOR);
    ring.setAttribute('stroke-width', '24');
    // Match the inheritance-pathway edge highlight (opacity 0.5, same teal) in
    // Canvas.jsx so the two analysis cues read consistently.
    ring.setAttribute('opacity', '0.5');
    ring.setAttribute('pointer-events', 'none');
    g.appendChild(ring);
  });
}

// Hover-link (canvas → card): outline the analysis card(s) that reference the
// hovered pid. Cards opt in via the `supportingMemberIds` prop on RankCard,
// which tags them with `data-card-supports`. Pass on=false to clear.
export function highlightAnalysisCards(pid, on = true) {
  document.querySelectorAll('[data-card-supports]').forEach((el) => {
    const supports = (el.getAttribute('data-card-supports') || '').split(',');
    const match = on && pid != null && supports.includes(String(pid));
    el.style.outline = match ? `2px solid ${HOVER_COLOR}` : '';
    el.style.outlineOffset = match ? '1px' : '';
  });
}

// Accent-bordered summary card. `right` is an optional trailing element
// (e.g. a confidence/UPN chip); `lines` render as muted detail rows.
export const RankCard = ({
  accent = '#2ba9bf',
  title,
  meta,
  lines = [],
  right,
  supportingMemberIds
}) => {
  const ids = (supportingMemberIds || []).map(String).filter(Boolean);
  const linkProps = ids.length
    ? {
        'data-card-supports': ids.join(','),
        onMouseEnter: () => highlightCanvasNodes(ids, true),
        onMouseLeave: () => highlightCanvasNodes([], false)
      }
    : {};
  return (
    <Box
      {...linkProps}
      sx={{
        border: '1px solid rgba(46,55,71,0.10)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        mb: 0.5,
        bgcolor: 'rgba(46,55,71,0.02)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '.85rem' }}>
          {title}
        </Typography>
        {right}
      </Box>
      {meta && (
        <Typography sx={{ fontSize: '.7rem', color: 'text.secondary' }}>
          {meta}
        </Typography>
      )}
      {lines.map((l, i) => (
        <Typography
          key={i}
          sx={{ fontSize: '.7rem', color: 'text.secondary', mt: 0.25 }}
        >
          {l}
        </Typography>
      ))}
    </Box>
  );
};

// Small uppercase section label (e.g. "INHERITANCE PATTERN").
export const AiSubsectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mt: 1.5,
      mb: 0.5,
      px: 0.5
    }}
  >
    {children}
  </Typography>
);

export const Empty = ({ text }) => (
  <Typography sx={{ p: 1, fontSize: 13, color: 'text.secondary' }}>
    {text}
  </Typography>
);
